import React, { createContext, useState } from 'react';
import type { CreatePayload } from '@atlaskit/link-create';
import {
	type CreateContentModalRuntimeProps,
	type CreateMenuContextType,
	LINK_CREATE_DRAFT_STATUS,
	CONTENT_TYPE,
} from '../../common/types/types';
import { ACCESS_REQUEST_CAPABILITIES } from '../utils';

const defaultWhiteboardData: CreatePayload = {
	url: '',
	objectId: '',
	objectType: '',
};

export const ContentMenuContext = createContext<CreateMenuContextType>({
	activePageUrl: '',
	accessStatus: ACCESS_REQUEST_CAPABILITIES.ACCESS_EXISTS,
	isCreateContentModalOpen: false,
	embeddedConfluenceSource: '',
	isEpModalOpen: false,
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	setIsCreateContentModalOpen: (_value: boolean) => {},
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	setActivePageUrl: (_value: string) => {},
	createContentRuntimeProps: {
		isOpen: true,
		contentType: CONTENT_TYPE.PAGE,
	},
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	setCreateContentRuntimeProps: (_value: CreateContentModalRuntimeProps) => {},
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	setIsEpModalOpen: (_value: boolean) => {},
	isEpModalLoading: false,
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	setIsEpModalLoading: (_value: boolean) => {},
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	setAccessStatus: (_value: string) => {},
	userHasConfluenceAccess: false,
	activeWhiteboardData: defaultWhiteboardData,
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	setActiveWhiteboardData: (_value: CreatePayload) => {},
	isEmbeddedWhiteboardModalOpen: false,
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	setEmbeddedWhiteboardModalOpen: (_value: boolean) => {},
	isCrossFlowModalOpen: false,
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	setIsCrossFlowModalOpen: (_value: boolean) => {},
	isPublishModalEnabled: false,
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	setIsPublishModalEnabled: (_value: boolean) => {},
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	setPageSpaceName: (_value: string) => {},
	pageSpaceName: '',
});

export interface CreateConfluenceContentMenuProviderProps {
	children?: React.ReactNode;
	embeddedConfluenceSource?: string;
	hasConfluenceAccess: boolean;
}

export const CreateConfluenceContentMenuProvider = ({
	children,
	embeddedConfluenceSource = 'jiraIssueView',
	hasConfluenceAccess,
}: CreateConfluenceContentMenuProviderProps) => {
	const [isCreateContentModalOpen, setIsCreateContentModalOpen] = useState(false);
	const [isEpModalOpen, setIsEpModalOpen] = useState(false);
	const [isEmbeddedWhiteboardModalOpen, setEmbeddedWhiteboardModalOpen] = useState(false);
	const [isCrossFlowModalOpen, setIsCrossFlowModalOpen] = useState(false);
	const [activePageUrl, setActivePageUrl] = useState('');
	const [pageSpaceName, setPageSpaceName] = useState('');
	const [activeWhiteboardData, setActiveWhiteboardData] = useState(defaultWhiteboardData);
	const [createContentRuntimeProps, setCreateContentRuntimeProps] =
		useState<CreateContentModalRuntimeProps>({
			onCreateBehavior: LINK_CREATE_DRAFT_STATUS, // Only used for Page
			isOpen: true,
			contentType: CONTENT_TYPE.PAGE,
		});
	const [accessStatus, setAccessStatus] = useState('');
	const [isPublishModalEnabled, setIsPublishModalEnabled] = useState(false);
	const [isEpModalLoading, setIsEpModalLoading] = useState(false);

	return (
		<ContentMenuContext.Provider
			value={{
				accessStatus,
				setAccessStatus,
				isCreateContentModalOpen,
				setIsCreateContentModalOpen,
				activePageUrl,
				setActivePageUrl,
				createContentRuntimeProps,
				setCreateContentRuntimeProps,
				isEpModalOpen,
				setIsEpModalOpen,
				isEpModalLoading,
				setIsEpModalLoading,
				embeddedConfluenceSource,
				userHasConfluenceAccess: hasConfluenceAccess,
				activeWhiteboardData,
				setActiveWhiteboardData,
				isEmbeddedWhiteboardModalOpen,
				setEmbeddedWhiteboardModalOpen,
				isCrossFlowModalOpen,
				setIsCrossFlowModalOpen,
				isPublishModalEnabled,
				setIsPublishModalEnabled,
				pageSpaceName,
				setPageSpaceName,
			}}
		>
			{children}
		</ContentMenuContext.Provider>
	);
};
